'use client';

import { ADVISORS_MATCH_SKU } from '@ria-client/advisor-search/constants';
import {
  AnalyticsSkuViewed,
  useAnalyticsSkuViewedContext,
} from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import { awaitTrack } from '@ria-client/segment/Analytics/awaitTrack';
import { useAnonymousId } from '@ria-client/segment/components/AnonymousIdProvider';
import { BrandIcon, type BrandIconName } from '@ria-client/tw-shared-ui';
import { buildAdvisorMatchPathUrl } from '@ria-client/utils/advisorMatch/urls';
import { forwardRef } from 'react';
import type { HeroBannerData } from '../HeroBanner/hero-banner';
import { buildAdUrl } from '../lib/use-ad-url';
import { NWAAdBug } from './components/nwa-ad-bug';

const DEFAULT_BUTTON_URL = buildAdvisorMatchPathUrl('age');

// Tracking name (impression section and `utm_campaign`). Legacy
// NerdwalletAdvisorsCustomizableTileEntrypoint tracks under its own name.
const componentName = ({ heroBannerCTA }: HeroBannerData) =>
  heroBannerCTA === 'nwa_custom_tile_entrypoint' ? 'RIA Custom Tile Hero' : 'RIA Tile Hero';

export interface AdsTileEntrypointProps {
  data: HeroBannerData;
}

export function AdsTileEntrypoint({ data }: AdsTileEntrypointProps) {
  return (
    <AnalyticsSkuViewed
      impressionPosition={0}
      impressionSection={componentName(data)}
      level={ADVISORS_MATCH_SKU.skuLevel}
      monetizable={ADVISORS_MATCH_SKU.monetizable}
      name={ADVISORS_MATCH_SKU.skuName}
      productId={ADVISORS_MATCH_SKU.productId}
      productName={ADVISORS_MATCH_SKU.productName}
      productType={ADVISORS_MATCH_SKU.skuProductType}
      source={ADVISORS_MATCH_SKU.skuSource}
    >
      <AdsTileEntrypointInner data={data} />
    </AnalyticsSkuViewed>
  );
}

const AdsTileEntrypointInner = forwardRef<HTMLDivElement, AdsTileEntrypointProps>(
  function AdsTileEntrypointInner({ data }, ref) {
    const anonymousId = useAnonymousId();
    const { fireShoppingFinishedEvent } = useAnalyticsSkuViewedContext();
    const tiles = data.heroComponentTileGroup ?? [];
    // Base resolves relative/missing CMS links instead of throwing mid-render. Not
    // usePathname(): on /best it returns the internal /best-root rewrite.
    const sourcePath = new URL(data.link ?? '', 'https://www.nerdwallet.com').pathname;

    const buildDestinationUrl = (baseUrl: string, tileContent: string) =>
      buildAdUrl(baseUrl, sourcePath, anonymousId, {
        utm_campaign: componentName(data),
        utm_content: tileContent,
        utm_medium: 'nerdwallet',
      });

    const navigateAfterTrack = async (destinationUrl: string, ctaType: string) => {
      await awaitTrack(
        fireShoppingFinishedEvent({
          ctaType,
          destinationUrl,
          finishType: 'external_application',
        }),
      ).catch(() => undefined);
      window.location.href = destinationUrl;
    };

    const handleClick = (
      event: React.MouseEvent<HTMLAnchorElement>,
      baseUrl: string,
      label: string,
    ) => {
      event.preventDefault();
      void navigateAfterTrack(buildDestinationUrl(baseUrl, label), label);
    };

    const baseUrl = data.heroBannerCTAUrl || DEFAULT_BUTTON_URL;

    return (
      <div
        className="flex w-fit flex-col gap-2 rounded-[0.5rem] bg-[var(--content-blue-lightest)]"
        data-testid="ads-tile-entrypoint"
        ref={ref}
      >
        <div className="flex flex-col gap-7 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="typography-headline font-medium">{data.heroBannerTitle}</h2>
            <div className="flex flex-col items-end gap-2.5 sm:flex-row sm:items-center">
              <NWAAdBug />
              {/* Remote logo does not need Next image optimization. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt="NWA Logo"
                src="https://cdns3.nerdwallet.com/nerdwallet-advisors/nwa-match-small-logo.png"
                width={75}
              />
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {tiles.map(tile => (
              <a
                className="flex min-h-32 w-[130px] flex-col items-center justify-center rounded-[4px] border border-[var(--content-blue-light)] bg-white text-black no-underline hover:border-2 hover:border-[var(--content-blue)] sm:w-[170px]"
                href={buildDestinationUrl(baseUrl, tile.title)}
                key={tile.title}
                onClick={event => handleClick(event, baseUrl, tile.title)}
              >
                {tile.icon !== 'none' ? (
                  <BrandIcon name={tile.icon as BrandIconName} size={32} />
                ) : null}
                <span className="typography-body w-[90%] max-w-[70%] text-center sm:w-[70%]">
                  {tile.title}
                </span>
              </a>
            ))}
          </div>
        </div>
        <a
          className="flex flex-wrap items-center justify-between rounded-b-[0.5rem] bg-[var(--content-blue)] px-6 py-[13px] text-white no-underline"
          href={buildDestinationUrl(baseUrl, 'match for free')}
          onClick={event => handleClick(event, baseUrl, 'match for free')}
        >
          <span className="font-sans font-medium">
            ✨ Get matched to a financial advisor for free
          </span>
          <span className="font-sans text-xs leading-3.5 sm:typography-text-small">
            Powered by NerdWallet Advisory LLC
          </span>
        </a>
      </div>
    );
  },
);
